import React, { useLayoutEffect, useState } from 'react'
import DashboardLayout from '../../../layouts/Dashboard'
import { Col, Row, Form, Container, Button, Spinner, Image } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import api from '../../../services/api'
import { toast } from 'react-toastify'
import { errorApi } from '../../../utils/errorApi'
import { IMaskInput } from 'react-imask'
import UserCircle from '../../../assets/UserCircle.svg'
import { Lock } from 'phosphor-react'
import ChangePasswordModal from '../../../components/ChangePasswordModal'
import styles from './styles.module.css'
import { useLoading } from '../../../hooks/LoadingContext'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'

const AtendenteEditarUsuario: React.FC = () => {
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState<string>('')
  const [gender, setGender] = useState<string>('')
  const [phone, setPhone] = useState<string>('')
  const [birth, setBirth] = useState<string>('')

  const [showChangePasswordModal, setShowChangePasswordModal] = useState<boolean>(false)
  const [profile_picture_url, setProfilePictureUrl] = useState<string>('')

  const { isLoading, setIsLoading } = useLoading()
  const [profilePicture, setProfilePicture] = useState<File | null>()

  const navigate = useNavigate()

  const clearFileInput = (e: any) => {
    const filesData = new DataTransfer()
    e.target.files = filesData.files
  }

  const handlePictureChange = (e: any) => {
    if (e.target.files) {
      const file = e.target.files[0]
      const fileType = file.name.split('.')[1].toLowerCase()

      if (!['jpg', 'png', 'gif', 'jpeg'].includes(fileType)) {
        toast.error('A foto deve possuir formato .PNG, .JPG, .JPEG ou .GIF')
        clearFileInput(e)
        setProfilePicture(null)
        setProfilePictureUrl(UserCircle)
      } else {
        setProfilePicture(e.target.files[0])
        const url = URL.createObjectURL(e.target.files[0])
        setProfilePictureUrl(url)
      }
    }
  }

  useLayoutEffect(() => {
    async function loadData() {
      setIsLoading(true)
      try {
        const { data } = await api.get(`/attendant/attendants`)

        setId(data.user_id)
        setName(data.name)
        setGender(data.gender)
        setPhone(data.phone)
        if (data.birth) setBirth(data.birth.split('T')[0])
        if (data.profile_picture) setProfilePictureUrl(data.profile_picture.url)
        else setProfilePictureUrl(UserCircle)
      } catch (err) {
        toast.error('Falha ao buscar os dados! Atualize a página.')
      }
      setIsLoading(false)
    }
    loadData()
  }, [id, setIsLoading])

  const handleSubmit = async (event: any) => {
    event.preventDefault()

    setIsLoading(true)

    if (!name || !gender || !phone) {
      toast.error('Você precisa preencher todos os campos obrigatórios!')
      setIsLoading(false)
      return
    }

    const data = new FormData()
    const model = {
      name: name,
      gender: gender,
      phone: phone,
      birth,
    }

    data.append('data', JSON.stringify(model))
    if (profilePicture) data.append('image', profilePicture, profilePicture.name)

    const config = {
      headers: {
        'content-type': 'multipart/form-data',
      },
    }

    try {
      await api.put(`/attendant/attendants/${id}`, data, config)

      toast.info('Atendente atualizado com sucesso!', {
        autoClose: 3000,
      })
      setIsLoading(false)
      navigate('/atendente')
    } catch (err) {
      toast.error(errorApi(err, 'Falha ao atualizar o atendente! Verifique os dados.'))
      setIsLoading(false)
    }
  }

  const isValidModel = () => {
    return name && gender && phone
  }

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Editar Usuário">
      <LoadingSpinner text="Salvando informações. Aguarde..." />
      <Container>
        <h2 style={{ textAlign: 'center' }}>Atualizar dados do atendente</h2>
        <Col style={{ textAlign: 'center' }}>
          <Image className={styles.profile_picture} fluid={true} src={profile_picture_url} />
        </Col>
        <Col className={styles.form_container}>
          <Row className={styles.form_row}>
            <Col md={6} className={styles.form_col}>
              <Form.Label className={styles.form_label}>Nome*</Form.Label>
              <Form.Control
                className={styles.form_control}
                placeholder="Digite aqui o nome"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Col>
            <Col md={6} className={styles.form_col}>
              <Form.Label className={styles.form_label}>Foto de perfil (opcional)</Form.Label>
              <Form.Control
                className={styles.form_control}
                type="file"
                placeholder="Selecione a foto de perfil"
                onChange={(event) => {
                  handlePictureChange(event)
                }}
              />
            </Col>
          </Row>

          <Row className={styles.form_row}>
            <Col md={6} className={styles.form_col}>
              <Form.Label className={styles.form_label}>Gênero*</Form.Label>
              <Form.Select
                className={styles.form_control}
                value={gender}
                onChange={(event) => setGender(event.target.value)}
              >
                <option value="">Selecione o gênero</option>
                <option value="male">Masculino</option>
                <option value="female">Feminino</option>
                <option value="other">Outro</option>
              </Form.Select>
            </Col>
            <Col md={6} className={styles.form_col}>
              <Form.Label className={styles.form_label}>Telefone*</Form.Label>
              <Form.Control
                className={styles.form_control}
                as={IMaskInput}
                mask={[
                  { mask: '(00) 00000000' }, // para números fixos (8 dígitos)
                  { mask: '(00) 000000000' }, // para números de celular (9 dígitos)
                ]}
                placeholder="Digite aqui o telefone"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            </Col>
          </Row>

          <Row className={styles.form_row}>
            <Col md={6} className={styles.form_col}>
              <Form.Label className={styles.form_label}>Data de nascimento</Form.Label>
              <Form.Control
                className={styles.form_control}
                placeholder="Digite data de nascimento"
                type="date"
                value={birth}
                onChange={(event) => setBirth(event.target.value)}
              />
            </Col>
          </Row>

          <Row className={styles.form_row}>
            <Col></Col>
          </Row>
          <Row className={styles.form_row}>
            <Col className="d-flex justify-content-md-start justify-content-center">
              <Button
                className={styles.change_password_button}
                onClick={() => setShowChangePasswordModal(true)}
              >
                Alterar senha <Lock className={styles.lock_icon} size={16} weight={'bold'} />
              </Button>
            </Col>
          </Row>
          <Row>
            <Col
              style={{ textAlign: 'right' }}
              className="d-flex justify-content-md-end justify-content-center"
            >
              <Button
                className={styles.button}
                disabled={isLoading || !isValidModel()}
                onClick={handleSubmit}
              >
                {isLoading ? (
                  <Spinner animation="border" size="sm" className={styles.spinner_button} />
                ) : (
                  'Atualizar usuário'
                )}
              </Button>
            </Col>
          </Row>
        </Col>
      </Container>
      <ChangePasswordModal
        show={showChangePasswordModal}
        closeModal={() => setShowChangePasswordModal(false)}
        id={id}
      />
    </DashboardLayout>
  )
}

export default AtendenteEditarUsuario
